import { Fragment, type ReactNode } from 'react';
import { Warning } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { Link } from '../Link/Link';
import { TONE_TEXT } from '../tone';

export type SourceLineProps = {
  /**
   * The standard sentences, already translated and without a final full stop: the source, the
   * sample, the period ("JobCrawls index", "212 ads, 84 with pay", "1 to 30 Sep 2026").
   */
  parts: ReactNode[];
  /** What was left out and why ("Excluded: 128 ads without pay, 9 hourly rates"). */
  excluded?: ReactNode;
  /** When the data was last refreshed ("Updated 12 Oct 2026"). */
  updated?: ReactNode;
  /** The method page, rendered as the last item ("Method"). */
  method?: { href: string; label: ReactNode };
  /** A warning that the data is out of date, in the attention tone ("This snapshot is 3 weeks old"). */
  stale?: ReactNode;
  className?: string;
};

/**
 * The standard source line (guideline 12, Trust): "JobCrawls index. 212 ads, 84 with pay. 1 to 30
 * Sep 2026. Updated 12 Oct 2026. Method". Takes already-translated parts, so the component holds no
 * copy; it joins them as sentences. Goes in `ChartFrame`'s `source` slot.
 */
export function SourceLine({ parts, excluded, updated, method, stale, className }: SourceLineProps) {
  const sentences: ReactNode[] = [...parts, excluded, updated].filter(
    (p) => p !== undefined && p !== null && p !== false,
  );
  return (
    <span className={cx('flex flex-col gap-1', className)}>
      <span>
        {sentences.map((s, i) => (
          <Fragment key={i}>
            {i > 0 ? ' ' : null}
            {s}.
          </Fragment>
        ))}
        {method ? (
          <>
            {sentences.length ? ' ' : null}
            <Link href={method.href}>{method.label}</Link>
          </>
        ) : null}
      </span>
      {stale ? (
        <span className={cx('inline-flex items-center gap-1 font-semibold', TONE_TEXT.attention)}>
          <Icon icon={Warning} size="sm" weight="fill" />
          {stale}
        </span>
      ) : null}
    </span>
  );
}
